import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { Lang } from "@cervval.js/common/src/i18n/languages";
import { asAnyObject, getProp } from "@cervval.js/common/src/tools/any.tools";
import { pushAllIfNewAndDefined } from "@cervval.js/common/src/tools/collection.tools";
import { ItemPropertyOrFunction, keyof } from "@cervval.js/common/src/tools/object.tools";
import { anyReallyAny, ObjectTools } from "@cervval.js/common/tools";
import { PropertyInfos, TypeInfos } from "@cervval.js/ts-reflect";

export type ForeignKey<ET, T, ForeignKeyET = ET> = keyof ForeignKeyET | {
    /** Table name. Function is not handled yet. */
    table: ObjectTools.ItemValueOrFunction<unknown, keyof ForeignKeyET>,
    /** Weak foreign key. See signification in SQLTableDefinition. */
    weak?: boolean
};

export const DTO_MODEL_SERVICE = "DTOModelService";

//#region Types ---------------------------------------------------------------

/** 
 * Foreign key definitions for one type 
 */
export type ForeignKeys<ET, T, ForeignKeyET = ET> = Partial<Record<keyof T, ForeignKey<ET, T, ForeignKeyET>>>;

/** 
 * Foreign key definitions for one type 
 */
export type ElementAssociations<ET, T extends keyof ET, ForeignKeyET = ET> = Partial<
    Record<keyof ET[T], ElementAssociation<ET, T, ForeignKeyET>>
>;

export interface AssociateColumn {
    /** Name of column */
    name: string;
}

export interface AssociateColumnTable<ForeignKeyET> extends AssociateColumn {
    /** Name of foreign key column, table should be defined in DB */
    foreignTable: keyof ForeignKeyET;
}

export interface AssociateColumnFieldType extends AssociateColumn {
    /** ex: ID, UID, STRING, ... */
    fieldType: string;
}

/** Represents a property that is an associative table */
export type ElementAssociation<ET, T extends keyof ET, ForeignKeyET = ET> = {
    name: keyof ForeignKeyET,
    ownerColumn: {
        name: string,
        foreignTable: T
    },
    /**
     * Use foreignTable typing if associateColumn is a table defined in DB
     * If associate column is not in DB, give fieldType only
     * Ex: For cjs permissions, PermissionDTO isn't in DB, can't given a foreignTable
     */
    associateColumn: AssociateColumnTable<ForeignKeyET> | AssociateColumnFieldType
}

/** Global information on a type */
export type ElementTypeInfo<ET, TableName extends keyof ET, ForeignKeyET = ET, ExtraType extends ET[TableName] = never> = {
    /** Use typeInfos on the DTO to fill this field */
    typeInfos: TypeInfos<ET[TableName]>,
    /** 
     * List of keys from the object (optional).
     * If specified, can be used to get the list of properties in a custom order.
     */
    keys?: (keyof ET[TableName] | keyof ExtraType)[],
    /** 
     * The foreign keys must be filled manually since they cannot be determined automatically 
     */
    foreignKeys?: ForeignKeys<ET, ET[TableName], ForeignKeyET>,
    /**
     * Property used for the id field.
     * Use null for no id, undefined to use default value.
     */
    idField?: keyof ET[TableName] | null,
    /**
     * Property used for the archived field.
     * Use null for no archive, undefined to use default value.
     */
    archivedField?: keyof ET[TableName] | null,

    // -- Association --
    /** 
     * The table associations keys must be filled manually since they cannot be determined automatically 
     */
    associations?: ElementAssociations<ET, TableName, ForeignKeyET>,

    // -- Extra properties --
    // We allow to add some extra information

    /** Extra typeInfos for other custom properties not stored in the DTO */
    extraTypeInfos?: TypeInfos<ExtraType>,
    /** Extra foreign keys */
    extraForeignKeys?: ForeignKeys<ET, ExtraType, ForeignKeyET>
};

/** Map of ElementTypeInfo for each ET */
export type ETTypeInfos<ET, ForeignKeyET = ET> = { [K in keyof ET]: ElementTypeInfo<ET, K, ForeignKeyET> };

/** Locale definition for a field */
export type FieldI18N = {
    /** Label for the property config */
    label: string,
    /** Short version for the property config. Can be used for columns. */
    shortName?: string,
    /** Tooltip */
    tooltip?: string,
    /** Placeholder of label if property of label is rendered in a generatedForm */
    placeholder?: string
};
export type TableI18N = {
    /** Display name for the table */
    name: string
};

/** 
 * List of locales for a DTO.
 * You can specify a type U that extends T to add some custom properties that you want to add.
 */
export type DTOI18N<T, U extends T = T> = {
    [K in keyof Required<U>]: null | FieldI18N
};

/** A function that will return locales for the properties */
export type I18N<T, U extends T = T> = (lang?: Lang) => DTOI18N<T, U>;

//#endregion

/**
 * This service allows to collect all info about your Data Transferable Objects (DTO).
 * It can be used in other helpers/services.
 */
@Injectable(DTO_MODEL_SERVICE)
export class DTOModelService<ET, FT> { // ET stands for ElementTypes, FT for FieldTypes

    // -- Model infos --
    protected readonly _typeInfos: { [K in keyof ET]?: ElementTypeInfo<ET, K> } = {};
    protected readonly _tableDisplayNames: { [K in keyof ET]?: (lang?: Lang) => { [IK in K]: TableI18N } } = {};
    protected readonly _locales: { [K in keyof ET]?: I18N<ET[K]> } = {};
    /** Store how the DTOs should be displayed in app */
    protected readonly _dtoDisplayNames: { [K in keyof ET]?: ItemPropertyOrFunction<ET[K], string> } = {};

    constructor(
        public readonly _tableNames: (keyof ET)[],
        public readonly _fieldTypes: (keyof FT)[],
        protected _defaultIdField: string,
        protected _idType: keyof FT,
        /** it is better to leave default undefined and to set the archived field on each concerned ElementTypeInfo */
        protected _defaultArchivedField?: string
    ) {
    }

    //#region Register infos on types -----------------------------------------

    /** Register type infos for a type */
    public registerTypeInfo<TableName extends keyof ET>(tableName: TableName, infos: ElementTypeInfo<ET, TableName>): void {
        this._typeInfos[tableName] = infos;
    }

    /** 
     * Register type infos for all types 
     * ElementTypeInfo<ET, K, anyReallyAny> - anyReallyAny because associations don't need to be registered and don't matter here
     */
    public registerTypeInfos(globalInfos: { [K in keyof ET]: ElementTypeInfo<ET, K, anyReallyAny> }): void {
        for (const tableName in globalInfos) {
            this._typeInfos[tableName] = globalInfos[tableName];
        }
    }

    /** 
     * Register locales for a type.
     * Note that you can specify a type T that extends the original DTO to add more fields.
     * This is specially useful to add properties that can be displayed in forms but does not appear in the DTO.
     */
    public registerTypeLocales<TableName extends keyof ET, T extends ET[TableName] = ET[TableName]>(tableName: TableName, i18n: I18N<ET[TableName], T>): void {
        this._locales[tableName] = i18n;
    }

    /**
     * Register locales for all types (see above for details).
     */
    public registerTypesLocales(globalI18N: { [K in keyof ET]: (I18N<ET[K]> | null) }): void {
        for (const tableName in globalI18N) {
            const i18n = globalI18N[tableName];
            if (i18n != null) {
                this._locales[tableName] = i18n as anyReallyAny;
            }
        }
    }

    /** register all tables names */
    public registerTableNames(tablesI18n: (lang?: Lang) => { [K in keyof ET]?: TableI18N }): void {
        // -- Test all tables handled by the locale function --
        const tmpI18n = tablesI18n(); // Warning, we don't store the result to be independant of the lang
        // Register the callback for each table that was returned by the i18n function
        for (const tableName in tmpI18n) {
            this._tableDisplayNames[tableName] = tablesI18n as anyReallyAny; // Here we know that the function will return the table's locale
        }
    }

    /** Register the DTO display name for a given table */
    public registerDTODisplayName(table: keyof ET, displayName: ItemPropertyOrFunction<ET[keyof ET], string>): void {
        this._dtoDisplayNames[table] = displayName;
    }

    /** Register all DTO display names */
    public registerDTODisplayNames(displayNames: { [K in keyof ET]?: ItemPropertyOrFunction<ET[K], string> }): void {
        for (const table in displayNames) {
            this._dtoDisplayNames[table] = displayNames[table];
        }
    }

    //#endregion

    //#region Inspect model ---------------------------------------------------

    /** Get the list of tables (don't expect any specific order) */
    public get tableNames(): (keyof ET)[] {
        return Object.keys(this._typeInfos) as (keyof ET)[];
    }

    /** Just for typings, to know if table exists use hasTable */
    public isTable(table: keyof ET): keyof ET {
        return table;
    }

    /** Check if table exists */
    public hasTable(table: string): boolean {
        return getProp(this._typeInfos, table) !== undefined;
    }

    /** Check if field exists in given table */
    public tableHasField(table: keyof ET, field: string): boolean {
        return this._typeInfos[table]?.typeInfos.properties?.[field] != null;
    }

    /**
     * Returns the type infos for the given table.
     * 
     * @throws If the table name does not exists
     */
    public getTableTypeInfos<TableName extends keyof ET>(tableName: TableName, options?: { withExtras?: boolean }): TypeInfos<ET[TableName]> {
        const kInfos = this._typeInfos[tableName];
        if (!kInfos) {
            throw `Unknown table ${tableName as string}`;
        }

        const kTypeInfos: TypeInfos<ET[TableName]> = {
            type: tableName as string,
            properties: {
                ...kInfos.typeInfos.properties,
                ...options?.withExtras !== true ? undefined : kInfos.extraTypeInfos?.properties
            }
        };
        return kTypeInfos;
    }

    /** 
     * Get the list of fields (properties) for the type.
     * 
     * @throws If type infos cannot be retrieved for table.
     */
    public getTableFields<TableName extends keyof ET>(table: TableName, options?: { withExtras?: boolean }): string[] {
        const typeInfos = this._typeInfos[table];
        if (!typeInfos) {
            throw `Unknown type: ${String(table)}`;
        }
        const keys: string[] = [...(typeInfos.keys ?? Object.keys(typeInfos.typeInfos.properties ?? {})) as string[]];
        if (options?.withExtras === true && typeInfos.extraTypeInfos?.properties) {
            pushAllIfNewAndDefined(keys, Object.keys(typeInfos.extraTypeInfos.properties));
        }
        return keys;
    }

    /** 
     * Return the id field of the type or undefined if no id.
     * 
     * @throws If there is no type infos for the tableName
     * @throws If the field does not exists in the type infos (if you don't want the default archived field, use null).
     */
    public getTableIdField<TableName extends keyof ET>(tableName: TableName): keyof ET[TableName] | undefined {
        const customIdField = this._typeInfos[tableName]?.idField;
        if (customIdField === null) {
            return undefined;
        }
        const idField = (customIdField ?? this._defaultIdField) as keyof ET[TableName];
        if (idField != null) {
            // Check that the property really exists
            const idFieldType = this.getFieldType(tableName, idField);
            if (idFieldType == null) {
                throw `Unknown property ${tableName as string}.${idField as string}`;
            }
        }
        return <keyof ET[TableName]>idField;
    }

    /** @returns true is field is the id */
    public isTableIdField<TableName extends keyof ET>(tableName: TableName, field: keyof ET[TableName]): boolean {
        const idField = this.getTableIdField(tableName);
        if (idField == null) {
            // This type has no id field
            return false;
        }
        return idField === field;
    }

    /** 
     * Return the archived field of the type or undefined if there is no such field.
     * 
     * @throws If there is no type infos for the tableName
     * @throws If the field does not exists in the type infos (if you don't want the default archived field, use null).
     */
    public getTableArchivedField<TableName extends keyof ET>(tableName: TableName): keyof ET[TableName] | undefined {
        const customArchivedField = this._typeInfos[tableName]?.archivedField;
        if (customArchivedField === null) {
            return undefined;
        }
        const archivedField = (customArchivedField ?? this._defaultArchivedField) as keyof ET[TableName];
        if (archivedField != null) {
            // Check that the property really exists
            const archivedFieldType = this.getFieldType(tableName, archivedField);
            if (archivedFieldType == null) {
                throw `Unknown property ${tableName as string}.${archivedField as string}`;
            }
        }
        return archivedField;
    }

    /** @returns true is field is the archived field */
    public isTableArchivedField<TableName extends keyof ET>(tableName: TableName, field: keyof ET[TableName]): boolean {
        const archivedField = this.getTableArchivedField(tableName);
        if (archivedField == null) {
            // This type has no archived field
            return false;
        }
        return archivedField === field;
    }

    /** 
     * By default, it field is linked to a "native" type (string, number...) getFieldType will return undefined
     * This is the expected behavior since we want all fields to be properly typed (ex DURATION_IN_MINUTES instead of number)
     * @returns field type if known 
     */
    public getFieldType<TableName extends keyof ET>(tableName: TableName, field: keyof ET[TableName], options?: { acceptNativeType: boolean }): keyof FT | undefined {
        const propertyInfo: PropertyInfos | undefined = this._findFieldPropertyInfo(tableName, field);
        if (propertyInfo == null) {
            return undefined;
        }
        if (options?.acceptNativeType) {
            return (propertyInfo.declaredType ?? propertyInfo.type) as (keyof FT | undefined);
        }
        return propertyInfo.declaredType as (keyof FT | undefined);
    }

    /** @returns optional property of the field, undefined if field is unknown. */
    public isFieldOptional<TableName extends keyof ET>(tableName: TableName, field: keyof ET[TableName]): boolean | undefined {
        const propertyInfo: PropertyInfos | undefined = this._findFieldPropertyInfo(tableName, field);
        if (propertyInfo == null) {
            return undefined;
        }
        return !!propertyInfo.isOptional;
    }

    /** Get the foreign keys for a table */
    public getTableForeignKeys<TableName extends keyof ET>(tableName: TableName, options?: { withExtras?: boolean }): ForeignKeys<ET, ET[TableName]> {
        let foreignKeys: ForeignKeys<ET, ET[TableName]> = {
            ...this._typeInfos[tableName]?.foreignKeys
        };
        if (options?.withExtras) {
            foreignKeys = {
                ...foreignKeys,
                ...this._typeInfos[tableName]?.extraForeignKeys
            };
        }
        return foreignKeys;
    }


    /** Get the foreign keys for a table */
    public getTableAssociations<TableName extends keyof ET>(tableName: TableName): ElementAssociations<ET, TableName> {
        const associations: ElementAssociations<ET, TableName> = {
            ...this._typeInfos[tableName]?.associations
        };
        return associations;
    }

    /** Get the foreign keys for a table */
    public getTableFieldAssociation<TableName extends keyof ET>(tableName: TableName, field: keyof ET[TableName]): ElementAssociation<ET, TableName> | undefined {
        const associations = this.getTableAssociations(tableName);
        return associations[field];
    }

    /** 
     * Get the table pointed by association if any. 
     * @returns null if property is not an association, undefined if association is a field type or table name 
     */
    public getTableFieldAssociationForeignTable<TableName extends keyof ET>(tableName: TableName, field: keyof ET[TableName]): keyof ET | null | undefined {
        const association = this.getTableFieldAssociation(tableName, field);
        if (association == null) {
            return null;
        } else {
            if (keyof<AssociateColumnTable<unknown>>("foreignTable") in association.associateColumn) {
                return (association.associateColumn as AssociateColumnTable<ET>).foreignTable;
            } else {
                return undefined;
            }
        }
    }

    /** Get the foreign table name if any, undefined if it is not a foreign key */
    public getFieldForeignKey<TableName extends keyof ET>(tableName: TableName, field: keyof ET[TableName]): ObjectTools.ItemValueOrFunction<unknown, keyof ET> | undefined {
        const foreignTable = this._findFieldForeignKey(tableName, String(field));
        if (foreignTable == null) {
            return undefined;
        } else if (typeof foreignTable === "object") {
            return foreignTable.table;
        } else {
            return foreignTable;
        }
    }

    /** @returns true if field is defined as a weak foreign key */
    public isFieldWeakForeignKey<TableName extends keyof ET>(tableName: TableName, field: keyof ET[TableName]): boolean | undefined {
        const foreignTable = this._findFieldForeignKey(tableName, String(field));
        if (foreignTable == null) {
            return undefined;
        } else if (typeof foreignTable === "object") {
            return foreignTable.weak ?? false;
        } else {
            return false;
        }
    }

    protected _findFieldForeignKey<TableName extends keyof ET>(tableName: TableName, field: string): ForeignKey<ET, unknown> | undefined {
        let foreignTable: ForeignKey<ET, unknown> | undefined = asAnyObject(this._typeInfos)[tableName as string]?.foreignKeys?.[field] as ForeignKey<ET, unknown>;
        if (foreignTable == null) {
            // Seek in extras
            foreignTable = this._typeInfos[tableName]?.extraForeignKeys?.[field] as ForeignKey<ET, unknown>;
        }
        return foreignTable;
    }

    /** Quick access to the properties infos if any */
    protected _findFieldPropertyInfo<TableName extends keyof ET>(tableName: TableName, field: keyof ET[TableName]): PropertyInfos | undefined {
        const eti: ElementTypeInfo<ET, TableName> | undefined = this._typeInfos[tableName];
        return eti?.typeInfos?.properties?.[String(field)] ?? eti?.extraTypeInfos?.properties?.[String(field)];
    }

    //#endregion

    //#region Locales ---------------------------------------------------------

    /** Get the internationalization strings for a table */
    public getTableI18N<TableName extends keyof ET>(table: TableName, lang?: Lang): DTOI18N<ET[TableName]> | undefined {
        const callback = this._locales[table];
        if (!callback) {
            return undefined;
        } else {
            return callback(lang);
        }
    }

    /** Get the table display name. Falls back to the own table name */
    public getTableDisplayName<TableName extends keyof ET>(table: TableName, lang?: Lang): string {
        return this._tableDisplayNames[table]?.(lang)?.[table]?.name ?? (table as string);
    }

    /** Return the display name property or getter of a given table */
    public getDtoDisplayName(table: keyof ET): ItemPropertyOrFunction<ET[keyof ET], string> | undefined {
        return this._dtoDisplayNames[table];
    }
    //#endregion
}